import React, { useState, useEffect, useRef, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Modal,
  Typography,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import { PhoneInput } from "react-international-phone";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import "react-international-phone/style.css";
import { fetchDonors } from "../../redux/donorSlice";
import useAutoFocus from "../AutoTab";
import countriesData from "../../data/countries_states_cities.json";
import { logger } from "../../utils/logger";
import DonorLabelPreview from "../DonorLabelPreview";

const DonorForm = forwardRef(
  ({ initialData, selectedDonor, onSave, data }, ref) => {
    const [donor, setDonor] = useState(
      initialData ||
        selectedDonor || {
          donorId: "",
          salutation: "",
          initial: "",
          firstName: "",
          lastName: "",
          address: "",
          address1: "",
          address2: "",
          city: "",
          state: "Tamil Nadu",
          pincode: "",
          country: "India",
          panCardNo: "",
          mobile1Formatted: "", // only for UI display
          countrycode1: "+91", // for backend
          mobile1: "", // for backend
          mobile2Formatted: "", // only for UI display
          countrycode2: "+91", // for backend
          mobile2: "", // for backend
          mailId: "",
          status: "Complete",
          donorRemarks: "",
          prasad: false,
        }
    );
    const initialRef = useRef(null);
    const firstNameRef = useRef(null);
    const dispatch = useDispatch();
    const { donors } = useSelector((state) => state.donor);
    const firstNameOptions = donors.map((d) => d.firstName).filter(Boolean);
    const [firstNameOpen, setFirstNameOpen] = useState(false);
    const { user } = useSelector((state) => state.auth);
    const username = user?.username || "";
    const role = user?.role || "";
    const [errors, setErrors] = useState({});
    const [existingDonor, setExistingDonor] = useState(null);
    const [ismodalopen, setIsmodalOpen] = useState(false);
    const [modalPurpose, setModalPurpose] = useState("add");
    const [countryCode, setCountryCode] = useState("");
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [lastCheckedMobiles, setLastCheckedMobiles] = useState({
      mobile1: "",
      mobile2: "",
    });

    const [modalShown, setModalShown] = useState({
      mobile1: false,
      mobile2: false,
    });
    const { formRef, handleKeyDown, handleSelectChange } = useAutoFocus();

    useEffect(() => {
      const purpose = donor.donorId ? "edit" : "add";
      setModalPurpose(purpose);
      logger.info("Donor Form Modal Opened", {
        page: "Donors",
        username,
        role,
        purpose,
        donorId: donor.donorId || "N/A",
      });

      return () => {
        logger.info("Donor Form Modal Closed", {
          page: "Donors",
          username,
          role,
          purpose,
          donorId: donor.donorId || "N/A",
        });
      };
    }, [username, role, donor.donorId]);

    useEffect(() => {
      setCountries(countriesData);
      const selectedCountry = countriesData.find(
        (c) => c.name === (donor.country || "India")
      );
      if (selectedCountry) {
        setStates(selectedCountry.states || []);
        const selectedState = selectedCountry.states.find(
          (s) => s.name === donor.state
        );
        if (selectedState) {
          setCities(selectedState.cities || []);
        } else {
          setCities([]);
        }
      }
    }, [donor.country, donor.state]);

    // useEffect(() => {
    //   if (initialData) {
    //     setDonor(initialData);
    //   } else if (selectedDonor) {
    //     setDonor(selectedDonor);
    //   }
    // }, [initialData, selectedDonor]);
    useEffect(() => {
      const source = initialData || selectedDonor;
      if (!source) return;

      const formatPhone = (mobile, code) => {
        if (!mobile || !code) return "";
        const cleanCode = String(code).replace(/^\+/, "");
        return `+${cleanCode}${mobile}`;
      };

      setDonor({
        ...source,

        // formatted values for UI
        mobile1Formatted: formatPhone(source.mobile1, source.countrycode1),
        mobile2Formatted: formatPhone(source.mobile2, source.countrycode2),

        // backend-safe values
        countrycode1: source.countrycode1 || "91",
        countrycode2: source.countrycode2 || "91",
      });
    }, [initialData, selectedDonor]);

    useEffect(() => {
      dispatch(fetchDonors());
    }, [dispatch]);

    useEffect(() => {
      if (initialRef.current) {
        if (donor.salutation && !donor.initial) {
          document.getElementById("initial").focus();
          initialRef.current.focus();
        }
      }
    }, [donor.salutation, donor.initial]);

    const handlePhoneKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (donor.mobile1 && !donor.firstName) {
          firstNameRef.current?.focus();
        }
      }
    };

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      if (name === "mobile1" || name === "mobile2") {
        setModalShown((prev) => ({ ...prev, [name]: false }));
      }
      if (name === "country") {
        const selectedCountry = countries.find((c) => c.name === value);
        const updatedStates = selectedCountry?.states || [];
        setStates(updatedStates);
        setCities([]);

        setDonor((prev) => ({
          ...prev,
          country: value,
          state: "",
          city: "",
        }));
      } else if (name === "state") {
        const selectedState = states.find((s) => s.name === value);
        const updatedCities = selectedState?.cities || [];
        setCities(updatedCities);
        setDonor((prev) => ({
          ...prev,
          state: value,
          city: "",
        }));
      } else {
        setDonor((prevDonor) => ({
          ...prevDonor,
          [name]:
            type === "checkbox"
              ? checked
              : name === "initial"
              ? value.toUpperCase()
              : name === "mailId"
              ? value.toLowerCase()
              : value,
        }));
      }
      if (modalPurpose === "edit") {
        logger.info("Field Edited", {
          page: "Donors",
          field: name,
          value,
          donorId: donor.donorId,
          username,
          role,
        });
      }
    };
    // const handlePhoneChange = (value, countryData, fieldName) => {
    //   const dialCode = countryData.country?.dialCode;

    //   // Save formatted value for UI
    //   setDonor((prev) => ({
    //     ...prev,
    //     [`${fieldName}Formatted`]: value,
    //   }));

    //   // Clean backend value: digits only
    //   const cleaned = value.replace(/\D/g, "");

    //   // Remove dialCode digits from cleaned to get just the mobile number
    //   const mobileNumber = cleaned.startsWith(dialCode)
    //     ? cleaned.slice(dialCode.length)
    //     : cleaned;

    //   setDonor((prev) => ({
    //     ...prev,
    //     [fieldName]: mobileNumber,
    //     [fieldName.replace("mobile", "countrycode")]: dialCode,
    //   }));
    // };
    const handlePhoneChange = (value, countryData, fieldName) => {
      const dialCode = countryData?.country?.dialCode || "";

      // Save formatted value for UI
      setDonor((prev) => ({
        ...prev,
        [`${fieldName}Formatted`]: value,
      }));

      // Clean digits only
      const digitsOnly = value.replace(/\D/g, "");
      const mobileDigits =
        dialCode && digitsOnly.startsWith(dialCode)
          ? digitsOnly.slice(dialCode.length)
          : digitsOnly;

      setDonor((prev) => ({
        ...prev,
        [fieldName]: mobileDigits,
        [fieldName.replace("mobile", "countrycode")]: dialCode,
      }));
    };
    const validateField = (name, value) => {
      let error = "";
      let foundmobile = null;

      switch (name) {
        case "firstName":
          if (!value) {
            error = "First name is required.";
          }
          break;
        case "mobile1":
        case "mobile2":
          if (!donor.donorId) {
            // Check if field has actual digits (more than 3)
            const cleanValue = value ? String(value).trim() : "";
            const hasValidDigits = cleanValue.replace(/\D/g, '').length > 3;
            
            if (hasValidDigits) {
              // Only validate if there are actual digits
              try {
                const formattedValue =
                  name === "mobile1"
                    ? donor.mobile1Formatted
                    : donor.mobile2Formatted;

                const parsedNumber = parsePhoneNumberFromString(formattedValue);

                if (!parsedNumber || !parsedNumber.isValid()) {
                  error = "Invalid phone number.";
                }
              } catch (err) {
                error = "Invalid phone number format.";
              }
              
              // Check if both numbers exist and have valid digits and are the same
              if (donor.mobile1 && donor.mobile2) {
                const m1 = String(donor.mobile1).trim().replace(/^\+?\d{1,3}\s*/, '').trim();
                const m2 = String(donor.mobile2).trim().replace(/^\+?\d{1,3}\s*/, '').trim();
                
                const m1HasDigits = m1.replace(/\D/g, '').length > 3;
                const m2HasDigits = m2.replace(/\D/g, '').length > 3;
                
                if (m1HasDigits && m2HasDigits && m1 === m2) {
                  error = "Mobile 1 and 2 cannot be the same.";
                }
              }
              
              // Only check for existing donors if this field has valid digits
              if (!error) {
                const donorMobiles = [donor.mobile1, donor.mobile2]
                  .filter(Boolean)
                  .map(String)
                  .filter(m => m.replace(/\D/g, '').length > 3); // Filter only valid mobiles

                foundmobile = donors.find((existingDonor) => {
                  if (donor.donorId && donor.donorId === existingDonor.donorId)
                    return false;
                  const existingMobiles = [
                    existingDonor.mobile1,
                    existingDonor.mobile2,
                  ]
                    .filter(Boolean)
                    .map(String)
                    .filter(m => m.replace(/\D/g, '').length > 3); // Filter only valid mobiles
                  return donorMobiles.some((mobile) =>
                    existingMobiles.includes(mobile)
                  );
                });
                
                if (foundmobile && (!modalShown[name] || lastCheckedMobiles[name] !== value)) {
                  setExistingDonor(foundmobile);
                  setIsmodalOpen(true);
                  setModalShown((prev) => ({ ...prev, [name]: true }));
                  setLastCheckedMobiles((prev) => ({ ...prev, [name]: value }));
                }
              }
            }
            
            setErrors((prev) => ({ ...prev, [name]: error }));
          }
          break;
        case "status":
          if (!value) {
            error = "Status is required.";
          }
          break;
        case "mailId":
          if (value && !/\S+@\S+\.\S+/.test(value)) {
            error = "Please enter a valid email address.";
          }
          break;
        case "panCardNo":
          if (
            value &&
            !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase())
          ) {
            error = "Please enter a valid PAN number (e.g., ABCDE1234F).";
          }
          break;
        default:
          break;
      }
      if (error) {
        logger.error("Validation Error", {
          page: "Donors",
          field: name,
          value,
          error,
          username,
          role,
        });
      }
      return error;
    };

    const handleBlur = (e) => {
      const { name, value } = e.target;
      const error = validateField(name, value);

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));
    };

    // Handle form submission
    const handleSubmit = (e) => {
      e.preventDefault();
      logger.info("Donor Form Submission Started", {
        page: "Donors",
        username,
        role,
        donorId: donor.donorId || "N/A",
        purpose: modalPurpose,
      });
      let newErrors = {};
      Object.keys(donor).forEach((key) => {
        const error = validateField(key, donor[key]);
        if (error) {
          newErrors[key] = error;
        }
      });
      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) {
        logger.error("Donor Form Submission Failed - Validation Errors", {
          page: "Donors",
          errors: newErrors,
          username,
          role,
        });
        return;
      }

      if (modalPurpose === "add") {
        logger.info("New Donor Added", {
          page: "Donors",
          donorId: donor.donorId,
          username,
          role,
        });
      } else if (modalPurpose === "edit") {
        logger.info("Existing Donor Edited", {
          page: "Donors",
          donorId: donor.donorId,
          username,
          role,
          editedFields: Object.keys(donor),
        });
      }
      onSave(donor);
    };

    React.useImperativeHandle(ref, () => ({
      submitForm: () => handleSubmit({ preventDefault: () => {} }),
    }));

    return (
      <>
        <Grid container spacing={3}>
          {/* Left Side — Donor Form */}
          <Grid item xs={12} md={8}>
            <Box
              ref={formRef}
              component="form"
              onSubmit={handleSubmit}
              sx={{ marginTop: 2 }}
            >
              <Grid container spacing={2}>
                {/* Salutation */}
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth error={Boolean(errors.salutation)}>
                    <InputLabel>Salutation</InputLabel>
                    <Select
                      label="Salutation"
                      name="salutation"
                      value={donor.salutation}
                      onChange={(e) => {
                        handleChange(e);
                        handleSelectChange(e);
                      }}
                      onKeyDown={handleKeyDown}
                      onBlur={handleBlur}
                      autoFocus
                    >
                      <MenuItem value="">Select</MenuItem>
                      {(data.salutation || []).map((opt) => (
                        <MenuItem key={opt._id} value={opt.value}>
                          {opt.value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.salutation && (
                      <FormHelperText>{errors.salutation}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Initial */}
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    label="Initial"
                    name="initial"
                    id="initial"
                    ref={initialRef}
                    value={donor.initial}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>

                {/* First Name */}
                <Grid item xs={6} md={4}>
                  <Autocomplete
                    freeSolo
                    options={firstNameOptions}
                    inputValue={donor.firstName || ""}
                    onInputChange={(event, newValue, reason) => {
                      if (reason === "input") {
                        setDonor((prev) => ({ ...prev, firstName: newValue }));
                        setFirstNameOpen(newValue.length > 0);
                      }
                    }}
                    open={firstNameOpen}
                    onClose={() => setFirstNameOpen(false)}
                    renderOption={(props, option, { index }) => (
                      <li {...props} key={option + "-" + index}>
                        {option}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="First Name"
                        required
                        error={Boolean(errors.firstName)}
                        helperText={errors.firstName}
                        inputRef={firstNameRef}
                        onBlur={(e) => {
                          handleBlur(e);
                          setFirstNameOpen(false);
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Last Name */}
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={donor.lastName}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>

                {/*Mobile1*/}
                <Grid item xs={6} md={4}>
                  <FormControl fullWidth error={Boolean(errors.mobile1)}>
                    <PhoneInput
                      defaultCountry="in"
                      label="Mobile1"
                      name="mobile1"
                      // value={
                      //   donor.mobile1
                      //     ? `+${countryCode}${donor.mobile1}`
                      //     : `+${countryCode || "91"}`
                      // }
                      value={donor.mobile1Formatted || ""}
                      onChange={(value, countryData) =>
                        handlePhoneChange(value, countryData, "mobile1")
                      }
                      onKeyDown={handlePhoneKeyDown}
                      onBlur={handleBlur}
                      inputStyle={{
                        width: "100%",
                        height: "56px",
                        fontSize: "16px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                      }}
                      buttonStyle={{
                        border: "none",
                        background: "transparent",
                      }}
                      containerStyle={{ width: "100%" }}
                    />
                    {donor.mobile1 &&
                    donor.mobile1 !== `+${countryCode}` &&
                    errors.mobile1 ? (
                      <FormHelperText>{errors.mobile1}</FormHelperText>
                    ) : (
                      <FormHelperText> </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Mobile2 */}
                <Grid item xs={6} md={4}>
                  <FormControl fullWidth error={Boolean(errors.mobile2)}>
                    <PhoneInput
                      defaultCountry="in"
                      disableCountryGuess={true}
                      disableDropdown={false}
                      enableSearch={true}
                      label="Mobile2"
                      name="mobile2"
                      // value={
                      //   donor.mobile2
                      //     ? `+${countryCode}${donor.mobile2}`
                      //     : `+${countryCode || "91"}`
                      // }
                      value={donor.mobile2Formatted}
                      onChange={(value, countryData) =>
                        handlePhoneChange(value, countryData, "mobile2")
                      }
                      onKeyDown={handleKeyDown}
                      onBlur={handleBlur}
                      inputStyle={{
                        width: "100%",
                        height: "56px",
                        fontSize: "16px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        // paddingLeft: "50px",
                      }}
                      buttonStyle={{
                        border: "none",
                        background: "transparent",
                      }}
                      containerStyle={{ width: "100%" }}
                    />
                    {donor.mobile2 &&
                    donor.mobile2 !== `+${countryCode}` &&
                    errors.mobile2 ? (
                      <FormHelperText>{errors.mobile2}</FormHelperText>
                    ) : (
                      <FormHelperText> </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Email */}
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="mailId"
                    value={donor.mailId}
                    onChange={handleChange}
                    error={Boolean(errors.mailId)}
                    helperText={errors.mailId}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>

                {/* Address */}
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={donor.address}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    sx={{
                      height: "56px",
                      "& .MuiInputBase-root": { height: "56px" },
                    }}
                  />
                </Grid>

                {/* Address 1 */}
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    name="address1"
                    value={donor.address1}
                    onChange={handleChange}
                    error={Boolean(errors.address1)}
                    helperText={errors.address1}
                    autoComplete="off"
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>

                {/* Address 2 */}
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    name="address2"
                    value={donor.address2}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>

                {/* Country */}
                <Grid item xs={6} md={4}>
                  <Autocomplete
                    freeSolo
                    options={countries.map((c) => c.name)}
                    inputValue={donor.country || ""}
                    onInputChange={(event, newValue, reason) => {
                      if (reason === "input" || reason === "clear") {
                        const selectedCountry = countries.find(
                          (c) => c.name === newValue
                        );
                        const updatedStates = selectedCountry?.states || [];
                        setStates(updatedStates);

                        // Clear state and city if country changes
                        setDonor((prev) => ({
                          ...prev,
                          country: newValue,
                          state: "",
                          city: "",
                        }));
                        setCities([]);
                      }
                    }}
                    onChange={(event, newValue) => {
                      // Handle selection from dropdown
                      const selectedCountry = countries.find(
                        (c) => c.name === newValue
                      );
                      const updatedStates = selectedCountry?.states || [];
                      setStates(updatedStates);

                      setDonor((prev) => ({
                        ...prev,
                        country: newValue || "",
                        state: "",
                        city: "",
                      }));
                      setCities([]);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country"
                        onBlur={handleBlur}
                      />
                    )}
                  />
                </Grid>

                {/* State */}
                <Grid item xs={6} md={4}>
                  <Autocomplete
                    freeSolo
                    options={states.map((s) => s.name)}
                    inputValue={donor.state || ""}
                    onInputChange={(event, newValue, reason) => {
                      if (reason === "input" || reason === "clear") {
                        const selectedState = states.find(
                          (s) => s.name === newValue
                        );
                        const updatedCities = selectedState?.cities || [];
                        setCities(updatedCities);

                        setDonor((prev) => ({
                          ...prev,
                          state: newValue,
                          city: "",
                        }));
                      }
                    }}
                    onChange={(event, newValue) => {
                      // Handle selection from dropdown
                      const selectedState = states.find(
                        (s) => s.name === newValue
                      );
                      const updatedCities = selectedState?.cities || [];
                      setCities(updatedCities);

                      setDonor((prev) => ({
                        ...prev,
                        state: newValue || "",
                        city: "",
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        onBlur={handleBlur}
                      />
                    )}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={6} md={4}>
                  <Autocomplete
                    freeSolo
                    options={cities.map((c) => c.name)}
                    inputValue={donor.city || ""}
                    onInputChange={(event, newValue, reason) => {
                      if (reason === "input" || reason === "clear") {
                        setDonor((prev) => ({
                          ...prev,
                          city: newValue,
                        }));
                      }
                    }}
                    onChange={(event, newValue) => {
                      setDonor((prev) => ({
                        ...prev,
                        city: newValue || "",
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="City" onBlur={handleBlur} />
                    )}
                  />
                </Grid>

                {/* Pincode */}
                {/* <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    name="pincode"
                    value={donor.pincode}
                    onChange={(e) => {
                      const { value } = e.target;
                      if (/^\d*$/.test(value)) {
                        handleChange(e);
                      }
                    }}
                    error={Boolean(errors.pincode)}
                    helperText={errors.pincode}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  /> */}
                  <Grid item xs={6} md={4}>
  <TextField
    fullWidth
    label="Pincode"
    name="pincode"
    value={donor.pincode}
    onChange={(e) => {
      const { value } = e.target;

      if (/^\d{0,6}$/.test(value)) {   // only 0–6 digits
        handleChange(e);
      }
    }}
    error={Boolean(errors.pincode)}
    helperText={errors.pincode}
    onKeyDown={handleKeyDown}
    onBlur={handleBlur}
  />

                </Grid>
                {/* Status */}
                <Grid item xs={6} md={4}>
                  <FormControl fullWidth error={Boolean(errors.status)}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      name="status"
                      value={donor.status}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      onBlur={handleBlur}
                      required
                    >
                      <MenuItem value="">Select</MenuItem>
                      {(data.status || []).map((opt) => (
                        <MenuItem key={opt._id} value={opt.value}>
                          {opt.value}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.status && (
                      <FormHelperText>{errors.status}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    label="Donor Remarks"
                    name="donorRemarks"
                    value={donor.donorRemarks}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>
                {/* PAN Card Number */}
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    label="PAN No."
                    name="panCardNo"
                    value={donor.panCardNo}
                    error={Boolean(errors.panCardNo)}
                    helperText={errors.panCardNo}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                  />
                </Grid>

                {/* Prasad */}
                <Grid item xs={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="prasad"
                        checked={donor.prasad}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                      />
                    }
                    label="Prasad"
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Label Preview
            </Typography>
            <DonorLabelPreview donor={donor} />
          </Grid>
          {/* Existing Donor data */}
          <Modal
            open={ismodalopen}
            onClose={() => setIsmodalOpen(false)}
            aria-labelledby="modal-title"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 500,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 2,
              }}
            >
              <div
                className="modal-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <Typography variant="h6">The Existing Donor Found</Typography>
                <IconButton
                  onClick={() => setIsmodalOpen(false)}
                  color="error"
                  data-log="Close Icon Button"
                >
                  <CloseIcon />
                </IconButton>
              </div>
              {existingDonor && (
                <>
                  <div className="modal-body" style={{ padding: "16px" }}>
                    <Typography style={{ color: "red" }}>
                      {" "}
                      The Mobile Number Already exist with Donor{" "}
                      {existingDonor.donorId}{" "}
                    </Typography>
                    <p>
                      <strong>Name:</strong> {existingDonor.firstName}{" "}
                      {existingDonor.lastName}
                    </p>
                    <p>
                      <strong>Mobile:</strong> {existingDonor.mobile1}{" "}
                      {existingDonor.mobile2}
                    </p>
                    <p>
                      <strong>Address:</strong> {existingDonor.address}{" "}
                      {existingDonor.address1} {existingDonor.address2}
                    </p>{" "}
                  </div>
                </>
              )}
              <div
                className="modal-footer"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "16px",
                  borderTop: "1px solid #ddd",
                }}
              >
                <Button
                  onClick={() => setIsmodalOpen(false)}
                  variant="contained"
                  color="error"
                >
                  Close
                </Button>
              </div>
            </Box>
          </Modal>
        </Grid>
      </>
    );
  }
);

export default DonorForm;
